import type { OfficeLayoutV1 } from "spatial-contracts";

/**
 * Collaborative Lobby — Open-plan creative space.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────┐
 *   │                                                    │
 *   │    ┌────────────────┐    ┌─────────────────┐      │
 *   │    │   Brainstorm    │    │   Boardroom     │      │
 *   │    │   Pod           │    │                 │      │
 *   │    └───────  ────────┘    └──────  ─────────┘      │
 *   │                                                    │
 *   │              ◆ Open Lobby ◆                        │
 *   │                                                    │
 *   │    ┌─────────────────────────────────────┐        │
 *   │    │          Social Lounge              │        │
 *   │    └──────────────  ─────────────────────┘        │
 *   │                                                    │
 *   └────────────────────────────────────────────────────┘
 *
 *  Grid: 28 x 22 tiles @ 48px = 1344 x 1056 world pixels
 */
export const collaborativeLobbyLayout: OfficeLayoutV1 = {
  schemaVersion: "v1",
  layoutRevision: 2,
  width: 28,
  height: 22,
  tileSize: 48,
  themeId: "creative-loft",
  assetPackIds: ["basic-office"],
  spawnPoint: { x: 14, y: 11 },
  walls: [
    // ── Outer perimeter ──
    { id: "wall-n", startX: 0, startY: 0, endX: 27, endY: 0, isDoor: false },
    { id: "wall-s", startX: 0, startY: 21, endX: 27, endY: 21, isDoor: false },
    { id: "wall-w", startX: 0, startY: 0, endX: 0, endY: 21, isDoor: false },
    { id: "wall-e", startX: 27, startY: 0, endX: 27, endY: 21, isDoor: false },

    // ── Brainstorm Pod (cols 2-10, rows 2-6) ──
    { id: "bp-n", startX: 2, startY: 2, endX: 10, endY: 2, isDoor: false },
    { id: "bp-w", startX: 2, startY: 2, endX: 2, endY: 6, isDoor: false },
    { id: "bp-e", startX: 10, startY: 2, endX: 10, endY: 6, isDoor: false },
    { id: "bp-s1", startX: 2, startY: 6, endX: 5, endY: 6, isDoor: false },
    { id: "bp-door", startX: 6, startY: 6, endX: 6, endY: 6, isDoor: true },
    { id: "bp-s2", startX: 7, startY: 6, endX: 10, endY: 6, isDoor: false },

    // ── Boardroom (cols 14-24, rows 2-6) ──
    { id: "br-n", startX: 14, startY: 2, endX: 24, endY: 2, isDoor: false },
    { id: "br-w", startX: 14, startY: 2, endX: 14, endY: 6, isDoor: false },
    { id: "br-e", startX: 24, startY: 2, endX: 24, endY: 6, isDoor: false },
    { id: "br-s1", startX: 14, startY: 6, endX: 18, endY: 6, isDoor: false },
    { id: "br-door", startX: 19, startY: 6, endX: 19, endY: 6, isDoor: true },
    { id: "br-s2", startX: 20, startY: 6, endX: 24, endY: 6, isDoor: false },

    // ── Social Lounge (cols 2-24, rows 15-19) ──
    { id: "sl-n1", startX: 2, startY: 15, endX: 12, endY: 15, isDoor: false },
    { id: "sl-door", startX: 13, startY: 15, endX: 13, endY: 15, isDoor: true },
    { id: "sl-n2", startX: 14, startY: 15, endX: 24, endY: 15, isDoor: false },
    { id: "sl-w", startX: 2, startY: 15, endX: 2, endY: 19, isDoor: false },
    { id: "sl-e", startX: 24, startY: 15, endX: 24, endY: 19, isDoor: false },
    { id: "sl-s", startX: 2, startY: 19, endX: 24, endY: 19, isDoor: false },
  ],
  furniture: [
    // Brainstorm Pod
    { id: "bp-whiteboard", itemType: "desk", x: 4, y: 3, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 4, height: 1 } },
    // Boardroom table
    { id: "br-table", itemType: "desk", x: 16, y: 3, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 6, height: 2 } },
    // Open lobby desks
    { id: "lobby-desk-1", itemType: "desk", x: 4, y: 9, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "lobby-desk-2", itemType: "desk", x: 4, y: 11, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "lobby-desk-3", itemType: "desk", x: 12, y: 9, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "lobby-desk-4", itemType: "desk", x: 12, y: 11, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "lobby-desk-5", itemType: "desk", x: 20, y: 9, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "lobby-desk-6", itemType: "desk", x: 20, y: 11, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    // Lounge sofas
    { id: "sofa-1", itemType: "desk", x: 5, y: 17, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "sofa-2", itemType: "desk", x: 11, y: 17, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "sofa-3", itemType: "desk", x: 17, y: 17, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    // Plants
    { id: "plant-1", itemType: "plant", x: 1, y: 1, rotation: 0, collisionFootprint: { offsetX: 0.1, offsetY: 0.1, width: 0.8, height: 0.8 } },
    { id: "plant-2", itemType: "plant", x: 26, y: 1, rotation: 0, collisionFootprint: { offsetX: 0.1, offsetY: 0.1, width: 0.8, height: 0.8 } },
    { id: "plant-3", itemType: "plant", x: 1, y: 20, rotation: 0, collisionFootprint: { offsetX: 0.1, offsetY: 0.1, width: 0.8, height: 0.8 } },
    { id: "plant-4", itemType: "plant", x: 26, y: 20, rotation: 0, collisionFootprint: { offsetX: 0.1, offsetY: 0.1, width: 0.8, height: 0.8 } },
  ],
  rooms: [
    {
      id: "brainstorm-pod", name: "Brainstorm Pod", capacityLimit: 8,
      cells: Array.from({ length: 4 }, (_, y) => Array.from({ length: 8 }, (_, x) => ({ x: x + 3, y: y + 3 }))).flat()
    },
    {
      id: "boardroom", name: "Boardroom", capacityLimit: 12,
      cells: Array.from({ length: 4 }, (_, y) => Array.from({ length: 10 }, (_, x) => ({ x: x + 15, y: y + 3 }))).flat()
    },
    {
      id: "open-lobby", name: "Open Lobby", capacityLimit: 30,
      cells: Array.from({ length: 7 }, (_, y) => Array.from({ length: 26 }, (_, x) => ({ x: x + 1, y: y + 8 }))).flat()
    },
    {
      id: "social-lounge", name: "Social Lounge", capacityLimit: 16,
      cells: Array.from({ length: 4 }, (_, y) => Array.from({ length: 22 }, (_, x) => ({ x: x + 3, y: y + 16 }))).flat()
    },
  ],
  spawns: [
    { id: "spawn-default", name: "Main Spawn", x: 14, y: 11 }
  ]
};
